import { useEffect, useState } from "react";
import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import DeepIncludes from "@/components/DeepIncludes";
import { ArrowUpRight, Check } from "lucide-react";
import api from "@/lib/api";
import { SEO, Reveal, Eyebrow } from "@/components/motion";
import { SERVICE_DETAILS } from "@/data/content";

function CheckList({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-body">
          <Check size={16} className="mt-1 shrink-0 text-sage-dark" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const [service, setService] = useState(null);
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/extras")])
      .then(([s, e]) => {
        setService(s.data.find((x) => x.slug === slug) || null);
        setExtras(e.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (loading || !hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    return () => clearTimeout(t);
  }, [loading, hash]);

  if (loading) {
    return <main className="min-h-screen pt-40 px-8"><p className="font-mono text-xs uppercase tracking-[0.2em] text-faint">Loading...</p></main>;
  }
  if (!service) return <Navigate to="/services" replace />;

  const d = SERVICE_DETAILS[slug] || {};

  return (
    <main data-testid={`service-detail-${slug}`}>
      <SEO
        title={`${service.name} in London Zones 1 & 2 | HOMELARA`}
        description={`${service.tagline} ${service.short_desc} Professional ${service.name.toLowerCase()} across London Zones 1 & 2 by HOMELARA.`}
      />

      {/* Hero */}
      <section className="relative">
        <div className="img-frame h-[46vh] sm:h-[56vh] w-full overflow-hidden">
          <img src={service.image} alt={`${service.name} - HOMELARA professional cleaning London`} className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-blue-midnight/80 via-blue-midnight/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-10 sm:pb-14">
            <Reveal>
              <Eyebrow className="text-sage">{service.name}</Eyebrow>
              <h1 className="mt-3 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-canvas">
                {service.tagline}
              </h1>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <Reveal>
          <p className="max-w-2xl text-lg sm:text-xl leading-relaxed text-body">{service.long_desc}</p>
        </Reveal>

        {/* Ideal for (property/business) */}
        {d.idealFor && (
          <Reveal className="mt-16">
            <h2 className="font-serif text-3xl tracking-tight text-ink">Ideal for</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {d.idealFor.map((x) => (
                <span key={x} className="rounded-full border border-subtle bg-surface px-5 py-2.5 text-sm text-body">
                  {x}
                </span>
              ))}
            </div>
          </Reveal>
        )}

        {d.deepIncludes && <DeepIncludes data={d.deepIncludes} />}

        {/* Includes sections (home/deep) */}
        {d.sections && (
          <div className="mt-16 scroll-mt-28" id="whats-included">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{d.includesTitle}</h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {d.sections.map((sec, i) => (
                <Reveal key={sec.title} delay={i * 0.06}>
                  <div className="h-full rounded-3xl border border-subtle bg-surface p-8">
                    <h3 className="font-sans text-lg font-semibold text-blue">{sec.title}</h3>
                    <div className="mt-5">
                      <CheckList items={sec.items} />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            {d.note && (
              <Reveal className="mt-8">
                <p className="rounded-2xl border border-sage/40 bg-sage-light/50 p-6 text-sm leading-relaxed text-body">
                  {d.note}
                </p>
              </Reveal>
            )}
          </div>
        )}

        {/* Property: what we can help with + turnover */}
        {d.helpWith && (
          <Reveal className="mt-16">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{d.helpTitle}</h2>
            <div className="mt-8 grid gap-x-12 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              {d.helpWith.map((x) => (
                <p key={x} className="flex items-start gap-3 text-sm sm:text-base text-body">
                  <Check size={16} className="mt-1 shrink-0 text-sage-dark" /> {x}
                </p>
              ))}
            </div>
          </Reveal>
        )}

        {d.turnover && (
          <Reveal className="mt-16">
            <div className="rounded-3xl bg-blue-midnight p-8 sm:p-14 text-canvas">
              <Eyebrow className="text-sage">Turnover cleaning</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">{d.turnover.headline}</h2>
              <p className="mt-4 max-w-xl text-canvas/75">{d.turnover.text}</p>
              <div className="mt-8 grid gap-x-10 gap-y-3 sm:grid-cols-2">
                {d.turnover.items.map((x) => (
                  <p key={x} className="flex items-start gap-3 text-sm text-canvas/85">
                    <Check size={16} className="mt-0.5 shrink-0 text-sage" /> {x}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        {d.extrasNote && (
          <Reveal className="mt-10">
            <p className="rounded-2xl border border-sage/40 bg-sage-light/50 p-6 text-sm leading-relaxed text-body">
              {d.extrasNote}
            </p>
          </Reveal>
        )}

        {/* Business: included + tailored */}
        {d.included && (
          <Reveal className="mt-16">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{d.includesTitle}</h2>
            <div className="mt-8 grid gap-x-12 gap-y-3 sm:grid-cols-2">
              {d.included.map((x) => (
                <p key={x} className="flex items-start gap-3 text-sm sm:text-base text-body">
                  <Check size={16} className="mt-1 shrink-0 text-sage-dark" /> {x}
                </p>
              ))}
            </div>
          </Reveal>
        )}

        {d.tailoredText && (
          <Reveal className="mt-14">
            <div className="rounded-3xl border border-subtle bg-warmcard p-8 sm:p-12">
              <h2 className="font-serif text-3xl tracking-tight text-ink">{d.tailoredTitle}</h2>
              <p className="mt-4 max-w-2xl text-body leading-relaxed">{d.tailoredText}</p>
              {d.optional && (
                <div className="mt-8">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sage-dark">Optional services</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {d.optional.map((x) => (
                      <span key={x} className="rounded-full bg-surface border border-subtle px-4 py-2 text-sm text-body">{x}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Reveal>
        )}

        {/* Optional extras from API (home/deep) */}
        {d.extrasTitle && extras.length > 0 && (
          <Reveal className="mt-16">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{d.extrasTitle}</h2>
            <p className="mt-3 text-sm text-faint">Add any of these during booking.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {extras.filter((e) => !(d.excludeExtras || []).includes(e.id)).map((e) => (
                <span key={e.id} className="rounded-full border border-subtle bg-surface px-5 py-2.5 text-sm text-body">
                  {e.name}
                </span>
              ))}
              <span className="rounded-full border border-dashed border-sage px-5 py-2.5 text-sm text-sage-dark">
                Specific additional requests
              </span>
            </div>
          </Reveal>
        )}

        {/* Equipment */}
        {d.equipmentText && (
          <Reveal className="mt-14">
            <div className="rounded-3xl border border-subtle bg-surface p-8 sm:p-10">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{d.equipmentTitle}</h2>
              <p className="mt-4 max-w-2xl text-body leading-relaxed">{d.equipmentText}</p>
            </div>
          </Reveal>
        )}

        {/* CTA */}
        <Reveal className="mt-16">
          <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-blue p-8 sm:p-12 text-canvas sm:flex-row sm:items-center">
            <div>
              <h2 className="font-serif text-3xl tracking-tight">Come home to a space that feels cared for.</h2>
              <p className="mt-2 text-sm text-canvas/70">
                {slug === "business-cleaning" ? "Get in touch and we'll prepare a tailored proposal." : "See your fixed price and book online in a few simple steps."}
              </p>
            </div>
            <Link
              to={slug === "business-cleaning" ? "/contact" : `/book?service=${slug}`}
              data-testid="service-detail-book-cta"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-canvas px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-blue transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl"
            >
              {service.cta_label || "Book a Clean"} <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
